/**
 * Подпись Telegram (parse_mode HTML) без dangerouslySetInnerHTML.
 *
 * Подпись пишет агент, санитайзера в зависимостях SPA нет, поэтому HTML в
 * браузер не отдаётся: из текста выбираются только теги b, i, u, s, code и a
 * с href на http или https. Всё остальное — script, img, ссылки javascript:,
 * теги с атрибутами, незакрытые и перепутанные теги — остаётся видимым
 * текстом, а экранирование текста делает React.
 */

import { safeHttpUrl } from './url'

export type TelegramTag = 'b' | 'i' | 'u' | 's' | 'code' | 'a'

export type TelegramNode =
  | { type: 'text'; text: string }
  /** Неразрешённая разметка: показывается как есть, в копируемый текст не попадает */
  | { type: 'markup'; text: string }
  | { type: 'element'; tag: TelegramTag; href: string | null; children: TelegramNode[] }

interface OpenFrame {
  tag: TelegramTag
  href: string | null
  raw: string
  children: TelegramNode[]
}

const SIMPLE_TAGS: ReadonlySet<string> = new Set(['b', 'i', 'u', 's', 'code'])

/** Любая конструкция вида <tag …> или </tag>; атрибуты — без угловых скобок */
const TAG_PATTERN = /<(\/?)([a-zA-Z][a-zA-Z0-9-]*)([^<>]*)>/g

/** У ссылки разрешён ровно один атрибут — href в кавычках */
const HREF_PATTERN = /^\s+href\s*=\s*(?:"([^"]*)"|'([^']*)')\s*$/i

const NAMED_ENTITIES: Record<string, string> = { lt: '<', gt: '>', amp: '&', quot: '"' }

/** Сущности, которые понимает Telegram: &lt; &gt; &amp; &quot; и числовые */
export function decodeTelegramEntities(value: string): string {
  return value.replace(/&(#[xX][0-9a-fA-F]{1,6}|#\d{1,7}|lt|gt|amp|quot);/g, (whole, entity: string) => {
    if (!entity.startsWith('#')) {
      return NAMED_ENTITIES[entity] ?? whole
    }

    const hex = entity[1] === 'x' || entity[1] === 'X'
    const codePoint = hex ? parseInt(entity.slice(2), 16) : parseInt(entity.slice(1), 10)
    const invalid =
      !Number.isInteger(codePoint) ||
      codePoint <= 0 ||
      codePoint > 0x10ffff ||
      (codePoint >= 0xd800 && codePoint <= 0xdfff)

    return invalid ? whole : String.fromCodePoint(codePoint)
  })
}

function hrefOf(attributes: string): string | null {
  const match = HREF_PATTERN.exec(attributes)
  if (match === null) {
    return null
  }

  return safeHttpUrl(decodeTelegramEntities(match[1] ?? match[2] ?? ''))
}

function append(target: TelegramNode[], node: TelegramNode): void {
  if (node.type !== 'element') {
    if (node.text === '') {
      return
    }

    const last = target[target.length - 1]
    if (last !== undefined && last.type !== 'element' && last.type === node.type) {
      last.text += node.text
      return
    }
  }

  target.push(node)
}

/** Разбор подписи в дерево разрешённых элементов и текста */
export function parseTelegramHtml(input: string): TelegramNode[] {
  const root: TelegramNode[] = []
  const stack: OpenFrame[] = []
  const current = (): TelegramNode[] => (stack.length > 0 ? stack[stack.length - 1].children : root)
  let position = 0

  for (const match of input.matchAll(TAG_PATTERN)) {
    const raw = match[0]
    const isClosing = match[1] === '/'
    const name = (match[2] ?? '').toLowerCase()
    const attributes = match[3] ?? ''
    const index = match.index ?? 0

    append(current(), { type: 'text', text: decodeTelegramEntities(input.slice(position, index)) })
    position = index + raw.length

    if (isClosing) {
      const top = stack[stack.length - 1]
      if (top !== undefined && top.tag === name && attributes.trim() === '') {
        stack.pop()
        append(current(), { type: 'element', tag: top.tag, href: top.href, children: top.children })
      } else {
        append(current(), { type: 'markup', text: raw })
      }
      continue
    }

    if (name === 'a') {
      const href = hrefOf(attributes)
      // Ссылка внутри ссылки в Telegram невозможна, а в DOM ломает разметку
      if (href !== null && !stack.some((frame) => frame.tag === 'a')) {
        stack.push({ tag: 'a', href, raw, children: [] })
      } else {
        append(current(), { type: 'markup', text: raw })
      }
      continue
    }

    if (SIMPLE_TAGS.has(name) && attributes.trim() === '') {
      stack.push({ tag: name as TelegramTag, href: null, raw, children: [] })
      continue
    }

    append(current(), { type: 'markup', text: raw })
  }

  append(current(), { type: 'text', text: decodeTelegramEntities(input.slice(position)) })

  // Незакрытый тег не применяется: его разметка видна текстом, содержимое остаётся на месте
  while (stack.length > 0) {
    const frame = stack.pop() as OpenFrame
    const parent = current()
    append(parent, { type: 'markup', text: frame.raw })
    frame.children.forEach((child) => append(parent, child))
  }

  return root
}

function plainText(nodes: TelegramNode[]): string {
  return nodes
    .map((node) => {
      if (node.type === 'text') {
        return node.text
      }
      if (node.type === 'markup') {
        return ''
      }

      const label = plainText(node.children)
      if (node.tag !== 'a' || node.href === null) {
        return label
      }

      // Из буфера разметка не применится: адрес ссылки не должен потеряться
      const trimmed = label.trim()
      if (trimmed === '') {
        return node.href
      }
      if (trimmed === node.href || `${trimmed}/` === node.href) {
        return label
      }

      return `${label} (${node.href})`
    })
    .join('')
}

/**
 * Текст подписи без HTML-тегов для ручной публикации: вставленная из буфера
 * разметка в Telegram не применяется, теги остались бы в посте буквами.
 */
export function telegramHtmlToPlainText(input: string): string {
  return plainText(parseTelegramHtml(input))
}
